import React, { useState, useEffect } from 'react';
import { Check, Building2, AlertTriangle, Loader, ArrowRight } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useTheme } from '../context/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

export default function AccountSelectionScreen({ onComplete, currentStep = 2 }) {
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAvailableAccounts();
  }, []);

  const [mccGroups, setMccGroups] = useState([]);

  const fetchAvailableAccounts = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/auth/google/connections/available`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        const allConnections = data.connections || [];
        const mccAccounts = allConnections.filter(acc => acc.account_type === 'Manager');
        const clientAccounts = allConnections.filter(acc => acc.account_type !== 'Manager');
        
        // Build MCC groups for display
        const groups = [];
        const groupedIds = new Set();
        
        for (const mcc of mccAccounts) {
          const children = clientAccounts.filter(c => c.login_customer_id === mcc.customer_id);
          if (children.length > 0) {
            groups.push({ mcc, children });
            children.forEach(c => groupedIds.add(c.id));
          }
        }
        
        const orphans = clientAccounts.filter(c => !groupedIds.has(c.id));
        if (orphans.length > 0) {
          groups.push({ mcc: null, children: orphans });
        }
        
        setMccGroups(groups);
        setAvailableAccounts(clientAccounts);
        setSelectedIds([]);
      }
    } catch (err) {
      console.error('Error fetching available accounts:', err);
      setError('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const toggleAccount = (accountId) => {
    setSelectedIds(prev => 
      prev.includes(accountId)
        ? prev.filter(id => id !== accountId)
        : [...prev, accountId]
    );
  };

  const handleSave = async () => {
    if (selectedIds.length === 0) {
      alert('Please select at least one account to manage');
      return;
    }

    setSaving(true);
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/auth/google/connections/select`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ selectedAccountIds: selectedIds })
      });

      const data = await response.json();
      
      if (data.success) {
        console.log(`✅ Saved selection: ${data.selectedCount} accounts`);
        // Sync localStorage with the customer_ids of selected accounts
        const selectedCustomerIds = availableAccounts
          .filter(acc => selectedIds.includes(acc.id))
          .map(acc => acc.customer_id);
        localStorage.setItem('linkedGoogleAdsAccounts', JSON.stringify(selectedCustomerIds));
        onComplete(selectedIds.length);
      } else {
        setError(data.error || 'Failed to save selection');
      }
    } catch (err) {
      console.error('Error saving selection:', err);
      setError('Failed to save selection');
    } finally {
      setSaving(false);
    }
  };

  const selectTopBySpend = () => {
    // Sort by spend (assuming account objects have spend data)
    const sorted = [...availableAccounts].sort((a, b) => {
      // If we have spend data, use it. Otherwise just select first 5
      return 0; // For now, just select first 5
    });
    const top5 = sorted.slice(0, 5).map(acc => acc.id);
    setSelectedIds(top5);
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: theme.colors.bg.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <Loader className="animate-spin" size={48} color="#8b5cf6" />
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: theme.colors.bg.primary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      zIndex: 9999
    }}>
      <div style={{
        background: theme.colors.bg.secondary,
        borderRadius: '24px',
        padding: '3rem',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        border: `1px solid ${theme.colors.border.primary}`,
        boxShadow: theme.mode === 'light' 
          ? '0 20px 60px rgba(0, 0, 0, 0.1)'
          : '0 20px 60px rgba(0, 0, 0, 0.5)'
      }}>
        {/* Progress Indicator */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '12px',
          marginBottom: '2.5rem'
        }}>
          {[1, 2, 3].map((step) => (
            <React.Fragment key={step}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: step === currentStep 
                  ? theme.colors.brand.primary
                  : step < currentStep
                    ? theme.colors.status.success
                    : theme.colors.bg.tertiary,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '700',
                fontSize: '1.1rem'
              }}>
                {step < currentStep ? '✓' : step}
              </div>
              {step < 3 && (
                <div style={{
                  width: '40px',
                  height: '2px',
                  background: step < currentStep 
                    ? theme.colors.status.success 
                    : theme.colors.bg.tertiary,
                  alignSelf: 'center'
                }} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step Label */}
        <div style={{
          fontSize: '0.9rem',
          color: theme.colors.text.tertiary,
          marginBottom: '0.5rem',
          fontWeight: '600',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          textAlign: 'center'
        }}>
          Step 2 of 3
        </div>

        <h2 style={{
          color: theme.colors.text.primary,
          fontSize: '2rem',
          fontWeight: '700',
          marginBottom: '0.75rem',
          textAlign: 'center'
        }}>
          Select Accounts to Manage
        </h2>
        
        <p style={{
          color: theme.colors.text.secondary,
          fontSize: '1.05rem',
          marginBottom: '2rem',
          textAlign: 'center',
          lineHeight: '1.6'
        }}>
          We found <strong>{availableAccounts.length} Google Ads accounts</strong>. 
          Select which ones you want to manage in Bidoryx.
        </p>

        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '12px',
            padding: '1rem',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <AlertTriangle size={20} color="#ef4444" />
            <span style={{ color: '#ef4444', fontSize: '0.95rem' }}>{error}</span>
          </div>
        )}

        {/* Quick Actions */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '1.5rem',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={selectTopBySpend}
            style={{
              padding: '10px 20px',
              background: theme.colors.bg.tertiary,
              color: theme.colors.text.primary,
              border: `1px solid ${theme.colors.border.primary}`,
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = theme.colors.bg.hover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = theme.colors.bg.tertiary;
            }}
          >
            Select Top 5
          </button>
          
          <button
            onClick={() => setSelectedIds(availableAccounts.map(acc => acc.id))}
            style={{
              padding: '10px 20px',
              background: theme.colors.bg.tertiary,
              color: theme.colors.text.primary,
              border: `1px solid ${theme.colors.border.primary}`,
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = theme.colors.bg.hover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = theme.colors.bg.tertiary;
            }}
          >
            Select All ({availableAccounts.length})
          </button>
          
          <button
            onClick={() => setSelectedIds([])}
            style={{
              padding: '10px 20px',
              background: theme.colors.bg.tertiary,
              color: theme.colors.text.primary,
              border: `1px solid ${theme.colors.border.primary}`,
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = theme.colors.bg.hover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = theme.colors.bg.tertiary;
            }}
          >
            Clear All
          </button>
          
          <div style={{ 
            marginLeft: 'auto',
            padding: '10px 16px',
            background: 'rgba(139, 92, 246, 0.1)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '8px',
            color: theme.colors.brand.primary,
            fontSize: '0.9rem',
            fontWeight: '700'
          }}>
            {selectedIds.length} selected
          </div>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          {mccGroups.length > 0 ? (
            mccGroups.map((group, groupIdx) => (
              <div key={group.mcc ? group.mcc.customer_id : 'direct'} style={{ marginBottom: groupIdx < mccGroups.length - 1 ? '1.5rem' : 0 }}>
                {/* MCC Group Header */}
                {group.mcc && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '0.5rem 0.25rem',
                    marginBottom: '8px'
                  }}>
                    <Building2 size={16} color={theme.colors.brand.primary} />
                    <span style={{
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      color: theme.colors.brand.primary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      {group.mcc.account_name || `MCC ${group.mcc.customer_id}`}
                    </span>
                    <span style={{
                      fontSize: '0.75rem',
                      color: theme.colors.text.tertiary,
                      marginLeft: '4px'
                    }}>
                      ({group.children.length} accounts)
                    </span>
                  </div>
                )}
                {!group.mcc && mccGroups.length > 1 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '0.5rem 0.25rem',
                    marginBottom: '8px'
                  }}>
                    <span style={{
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      color: theme.colors.text.tertiary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Direct Accounts
                    </span>
                  </div>
                )}
                
                {/* Account Items */}
                {group.children.map(account => {
                  const isSelected = selectedIds.includes(account.id);
                  return (
                    <div
                      key={account.id}
                      onClick={() => toggleAccount(account.id)}
                      style={{
                        background: isSelected ? 'rgba(139, 92, 246, 0.1)' : theme.colors.bg.tertiary,
                        border: `2px solid ${isSelected ? theme.colors.brand.primary : theme.colors.border.primary}`,
                        borderRadius: '12px',
                        padding: '1.25rem',
                        marginBottom: '12px',
                        marginLeft: group.mcc ? '1.5rem' : 0,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem'
                      }}
                    >
                      <div style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '8px',
                        background: isSelected ? theme.colors.brand.primary : 'transparent',
                        border: `2px solid ${isSelected ? theme.colors.brand.primary : theme.colors.border.primary}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        transition: 'all 0.2s'
                      }}>
                        {isSelected && <Check size={18} color="white" strokeWidth={3} />}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          color: theme.colors.text.primary,
                          fontSize: '1.05rem',
                          fontWeight: '600',
                          marginBottom: '0.25rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px'
                        }}>
                          <Building2 size={18} color={isSelected ? theme.colors.brand.primary : theme.colors.text.tertiary} />
                          {account.account_name || account.customer_id}
                        </div>
                        <div style={{
                          color: theme.colors.text.tertiary,
                          fontSize: '0.9rem'
                        }}>
                          ID: {account.customer_id}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          ) : (
            availableAccounts.map(account => {
              const isSelected = selectedIds.includes(account.id);
              return (
                <div
                  key={account.id}
                  onClick={() => toggleAccount(account.id)}
                  style={{
                    background: isSelected ? 'rgba(139, 92, 246, 0.1)' : theme.colors.bg.tertiary,
                    border: `2px solid ${isSelected ? theme.colors.brand.primary : theme.colors.border.primary}`,
                    borderRadius: '12px',
                    padding: '1.25rem',
                    marginBottom: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                  }}
                >
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '8px',
                    background: isSelected ? theme.colors.brand.primary : 'transparent',
                    border: `2px solid ${isSelected ? theme.colors.brand.primary : theme.colors.border.primary}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all 0.2s'
                  }}>
                    {isSelected && <Check size={18} color="white" strokeWidth={3} />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      color: theme.colors.text.primary,
                      fontSize: '1.05rem',
                      fontWeight: '600',
                      marginBottom: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      <Building2 size={18} color={isSelected ? theme.colors.brand.primary : theme.colors.text.tertiary} />
                      {account.account_name || account.customer_id}
                    </div>
                    <div style={{
                      color: theme.colors.text.tertiary,
                      fontSize: '0.9rem'
                    }}>
                      ID: {account.customer_id}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Continue Button */}
        <button
          onClick={handleSave}
          disabled={saving || selectedIds.length === 0}
          style={{
            width: '100%',
            padding: '1.25rem 2rem',
            background: selectedIds.length > 0 
              ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
              : theme.colors.bg.tertiary,
            color: 'white',
            border: 'none',
            borderRadius: '16px',
            cursor: selectedIds.length > 0 ? 'pointer' : 'not-allowed',
            fontSize: '1.1rem',
            fontWeight: '700',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            boxShadow: selectedIds.length > 0 ? '0 8px 25px rgba(99, 102, 241, 0.4)' : 'none',
            transition: 'all 0.3s',
            marginTop: '1rem'
          }}
          onMouseEnter={(e) => {
            if (selectedIds.length > 0 && !saving) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 35px rgba(99, 102, 241, 0.5)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = selectedIds.length > 0 ? '0 8px 25px rgba(99, 102, 241, 0.4)' : 'none';
          }}
        >
          {saving ? (
            <>
              <Loader className="animate-spin" size={24} />
              Saving...
            </>
          ) : (
            <>
              Continue with {selectedIds.length} account{selectedIds.length !== 1 ? 's' : ''}
              <ArrowRight size={24} />
            </>
          )}
        </button>

        {/* Help Text */}
        <p style={{
          marginTop: '1.5rem',
          textAlign: 'center',
          fontSize: '0.85rem',
          color: theme.colors.text.tertiary
        }}>
          You can change this selection anytime in Settings
        </p>
      </div>
    </div>
  );
}

